import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, MapPin, Phone, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import StepIndicator from "@/components/ui/StepIndicator";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import PasswordInput from "@/components/ui/PasswordInput";

interface SignupData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone: string;
  location: string;
}

const MultiStepSignupForm: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { register } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<SignupData>({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    location: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<SignupData>>({});

  const totalSteps = 3;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof SignupData]) {
      setErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<SignupData> = {};

    switch (step) {
      case 1:
        if (!formData.email) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = "Please enter a valid email address";
        }
        if (!formData.password) newErrors.password = "Password is required";
        else if (formData.password.length < 8) {
          newErrors.password = "Password must be at least 8 characters";
        }
        if (!formData.confirmPassword) newErrors.confirmPassword = "Please confirm your password";
        else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = "Passwords do not match";
        }
        break;

      case 2:
        if (!formData.firstName) newErrors.firstName = "First name is required";
        if (!formData.lastName) newErrors.lastName = "Last name is required";
        if (!formData.phone) newErrors.phone = "Phone number is required";
        break;

      case 3:
        if (!formData.location) newErrors.location = "Location is required";
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setIsLoading(true);
    try {
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      await register(fullName, formData.email, formData.password);
      
      toast({
        title: "Account created successfully!",
        description: "Please check your email to verify your account.",
      });
      
      navigate("/verify-email");
    } catch (error: any) {
      toast({
        title: "Signup failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sauna-navy font-semibold mb-1 block">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                required
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="password" className="text-sauna-navy font-semibold mb-1 block">
                Password
              </Label>
              <PasswordInput
                id="password"
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                required
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-sauna-navy font-semibold mb-1 block">
                Confirm Password
              </Label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                required
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-sauna-navy font-semibold mb-1 block">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                  required
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                )}
              </div>
              <div>
                <Label htmlFor="lastName" className="text-sauna-navy font-semibold mb-1 block">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                  required
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="phone" className="text-sauna-navy font-semibold mb-1 block">
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={handleChange}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                required
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="location" className="text-sauna-navy font-semibold mb-1 block">
                Location
              </Label>
              <Input
                id="location"
                name="location"
                type="text"
                placeholder="City, State or Country"
                value={formData.location}
                onChange={handleChange}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                required
              />
              {errors.location && (
                <p className="text-red-500 text-sm mt-1">{errors.location}</p>
              )}
            </div>
            <div className="bg-sauna-beige/50 p-4 rounded-lg">
              <h4 className="font-semibold text-sauna-navy mb-2">Review Your Information</h4>
              <div className="text-sm text-sauna-navy/80 space-y-1">
                <p><strong>Email:</strong> {formData.email}</p>
                <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
                <p><strong>Phone:</strong> {formData.phone}</p>
                <p><strong>Location:</strong> {formData.location}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Create Your Account";
      case 2:
        return "Personal Information";
      case 3:
        return "Location & Review";
      default:
        return "Sign Up";
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1:
        return "Set up your email and password";
      case 2:
        return "Tell us about yourself";
      case 3:
        return "Where are you located?";
      default:
        return "Join our community";
    }
  };

  return (
    <div className="bg-sauna-beige min-h-screen flex items-start justify-center px-2 mt-4">
      <Card className="w-full max-w-md min-h-[600px] shadow-2xl border-none rounded-2xl p-0 sm:p-1 bg-white/90 flex flex-col justify-center">
        <CardHeader className="flex flex-col items-center pt-6 pb-4">
          <div className="rounded-full bg-gradient-to-b from-sauna-orange/80 to-sauna-orange p-4 mb-4 shadow-lg">
            <User className="h-10 w-10 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-sauna-navy mb-1 text-center">
            {getStepTitle()}
          </CardTitle>
          <CardDescription className="text-base text-sauna-navy/80 mb-2 text-center font-medium">
            {getStepDescription()}
          </CardDescription>
          <StepIndicator 
            currentStep={currentStep} 
            totalSteps={totalSteps} 
            className="mb-6"
            stepLabels={["Account", "Personal", "Location"]}
            showLabels={true}
          />
        </CardHeader>
        
        <CardContent className="px-4 pb-6 pt-0">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {renderStepContent()}
            
            <div className="flex gap-3 pt-4">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="flex-1 border-sauna-orange text-sauna-orange hover:bg-sauna-orange hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white hover:from-sauna-orange/90 hover:to-sauna-orange"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white hover:from-sauna-orange/90 hover:to-sauna-orange disabled:opacity-70"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </Button>
              )}
            </div>
            
            <div className="text-center mt-6 text-sauna-navy/80 text-base">
              Already have an account?{" "}
              <Link to="/login" className="text-sauna-orange font-semibold hover:underline">
                Sign in
              </Link>
            </div>
            
            <div className="text-xs text-center text-sauna-navy/60 mt-4">
              By creating an account, you agree to our{" "}
              <Link to="/terms-of-use" className="text-sauna-orange underline">
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link to="/privacy-policy" className="text-sauna-orange underline">
                Privacy Policy
              </Link>
              .
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiStepSignupForm; 